import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { AppointmentSection } from './AppointmentSection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Calendar, Eye, ArrowRight, Search } from 'lucide-react';
import { BlogSubscribeForm } from './BlogSubscribeForm';
import { Toaster } from './ui/sonner';

export const BlogsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);

  const categories = [
    { value: 'all', label: 'All Articles' },
    { value: 'quick_read', label: 'Quick Reads' },
    { value: 'fdi', label: 'FDI & India Entry' },
    { value: 'finance', label: 'Finance' },
    { value: 'tax', label: 'Tax' },
    { value: 'compliance', label: 'Compliance' },
    { value: 'payroll', label: 'Payroll' },
    { value: 'business_tips', label: 'Business Tips' },
    { value: 'case_study', label: 'Case Studies' }
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchBlogs();
  }, [selectedCategory]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      let url = `${BACKEND_URL}/api/blogs/?limit=50`;
      
      if (selectedCategory !== 'all') {
        url += `&category=${selectedCategory}`;
      }
      
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setBlogs(data.blogs || []);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      finance: 'bg-blue-100 text-blue-700',
      tax: 'bg-purple-100 text-purple-700',
      compliance: 'bg-green-100 text-green-700',
      payroll: 'bg-orange-100 text-orange-700',
      business_tips: 'bg-pink-100 text-pink-700',
      case_study: 'bg-indigo-100 text-indigo-700',
      fdi: 'bg-emerald-100 text-emerald-700',
      quick_read: 'bg-amber-100 text-amber-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (blog) => {
    const dateStr = blog.publishedAt || blog.createdAt;
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getReadTime = (blog) => {
    const wordCount = (blog.excerpt || '').split(/\s+/).length;
    return wordCount > 80 ? '3 min' : wordCount > 40 ? '2 min' : '1 min';
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    if (category === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category });
    }
  };

  const filteredBlogs = blogs.filter(blog => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      blog.title.toLowerCase().includes(query) ||
      blog.excerpt.toLowerCase().includes(query) ||
      blog.tags.some(tag => tag.toLowerCase().includes(query))
    );
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Virtual CFO Consultancy Finance Blog",
    "description": "Expert insights on finance, accounting, tax, compliance, and business growth",
    "url": `${window.location.origin}/blogs`,
    "publisher": {
      "@type": "Organization",
      "name": "NSRM Consultants India Private Limited",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "blogPost": blogs.map(blog => ({
      "@type": "BlogPosting",
      "headline": blog.title,
      "url": `${window.location.origin}/blog/${blog.slug}`,
      "datePublished": blog.publishedAt,
      "author": {
        "@type": "Person",
        "name": blog.author
      }
    }))
  };

  useEffect(() => {
    document.title = 'Finance & Accounting Blog | Expert Insights | Virtual CFO Consultancy';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <meta name="description" content="Stay updated with expert insights on finance, accounting, GST, tax compliance, payroll, and business growth strategies from certified Chartered Accountants." />
      <meta name="keywords" content="finance blog, accounting tips, GST compliance, tax planning, payroll management, virtual CFO, business finance, chartered accountant advice" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Finance & Accounting Blog | Virtual CFO Consultancy" />
      <meta property="og:description" content="Expert insights on finance, tax, compliance, and business growth" />
      <meta property="og:url" content={`${window.location.origin}/blogs`} />
      <link rel="canonical" href={`${window.location.origin}/blogs`} />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      <Header onBookAppointment={() => setIsAppointmentOpen(true)} />
      
      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-8 sm:pb-12 px-4 sm:px-6 bg-gradient-to-br from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
            Knowledge Hub
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
            Finance & Accounting
            <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Expert Insights
            </span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8">
            Bite-sized expert insights you can read in under 3 minutes. Actionable advice on GST, compliance, FDI, and business finance from certified CAs.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search articles by title, topic, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-4 py-6 text-lg rounded-full border-2 border-gray-200 focus:border-emerald-500"
            />
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="px-4 py-6 sm:py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
            {categories.map((cat) => (
              cat.value === 'all' ? (
                <Button
                  key={cat.value}
                  variant={selectedCategory === 'all' ? "default" : "outline"}
                  onClick={() => handleCategoryChange('all')}
                  className={selectedCategory === 'all' ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  data-testid={`category-filter-${cat.value}`}
                >
                  {cat.label}
                </Button>
              ) : (
                <Link key={cat.value} to={`/blogs/${cat.value}`}>
                  <Button
                    variant="outline"
                    data-testid={`category-filter-${cat.value}`}
                  >
                    {cat.label}
                  </Button>
                </Link>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Blogs Grid */}
      <section className="py-8 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={`skeleton-${i}`} className="animate-pulse">
                  <div className="h-48 bg-gray-200 rounded-t-xl"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600 mb-4">No articles found.</p>
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog) => (
                <Link 
                  key={blog.blogId}
                  to={`/blog/${blog.slug}`}
                  className="no-underline text-inherit"
                  data-testid={`blog-link-${blog.slug}`}
                >
                  <Card 
                    data-testid={`blog-card-${blog.slug}`}
                    className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer overflow-hidden h-full"
                  >
                    {blog.featuredImage && (
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={blog.featuredImage} 
                          alt={blog.title}
                          loading="lazy"
                          width="400"
                          height="192"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getCategoryColor(blog.category)}>
                          {blog.category === 'fdi' ? 'FDI' : blog.category.replace('_', ' ')}
                        </Badge>
                        <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">
                          {getReadTime(blog)} read
                        </Badge>
                      </div>
                      <CardTitle className="text-lg group-hover:text-emerald-600 transition-colors line-clamp-2">
                        {blog.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-2 text-sm">
                        {blog.excerpt}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDate(blog)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" />
                          <span>{blog.views}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {blog.tags.slice(0, 2).map((tag, idx) => (
                          <Badge key={`tag-${tag}-${idx}`} variant="outline" className="text-xs px-2 py-0">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      
                      <span className="inline-flex items-center text-sm font-medium group-hover:text-emerald-600 transition-colors">
                        Quick Read
                        <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="max-w-2xl mx-auto">
          <BlogSubscribeForm />
        </div>
      </section>

      <Footer onBookAppointment={() => setIsAppointmentOpen(true)} />
      <AppointmentSection 
        isOpen={isAppointmentOpen} 
        onClose={() => setIsAppointmentOpen(false)} 
      />
      <Toaster />
    </div>
  );
};